// File: src/components/MatchFlow/useMatchFlow.js

import { useContext, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { MatchFlowContext } from './MatchFlowContext';

export function useMatchFlow() {
  const {
    socket,
    match,
    setMatch,
    isFinding,
    setIsFinding,
    occupancy,
    setOccupancy,
    errorMsg,
    setErrorMsg,
  } = useContext(MatchFlowContext);

  const navigate = useNavigate();
  const FIND_GUARD_KEY = '__mf_manual_find_only__';

  /**
   * findPartner: Directly emits the "findPartner" event.
   */
  const findPartner = useCallback(
    (userId, eventId, sessionId) => {
      if (!socket) return;
      const stack =
        new Error().stack?.split('\n').slice(1, 5).join(' | ') || '';
      console.log('[useMatchFlow] => findPartner', {
        userId,
        eventId,
        from: stack,
      });
      try {
        const ok = sessionStorage.getItem(FIND_GUARD_KEY) === '1';
        if (!ok) {
          console.warn(
            '[useMatchFlow] findPartner blocked (not user-initiated). ' +
              'Set sessionStorage.__mf_manual_find_only__=1 before calling.'
          );
          return;
        }
      } catch {}
      socket.emit('findPartner', { userId, eventId, sessionId });
    },
    [socket]
  );

  /**
   * cancelFind: Directly emits the "cancelFind" event.
   */
  const cancelFind = useCallback(
    (userId, eventId, sessionId) => {
      if (!socket) return;
      console.log(
        '[useMatchFlow] => cancelFind => calling socket.emit("cancelFind")'
      );
      socket.emit('cancelFind', { userId, eventId, sessionId });
    },
    [socket]
  );

  /**
   * respondMatch: Emits a response (accepted/declined) for the match.
   */
  const respondMatch = useCallback(
    (matchId, userId, response, sessionId) => {
      if (!socket) return;
      console.log('[useMatchFlow] => respondMatch =>', response);
      socket.emit('respondMatch', { matchId, userId, response, sessionId });
    },
    [socket]
  );

  /**
   * initVerification: Requests the server to assign aggregator/codeHolder.
   */
  const initVerification = useCallback(
    (matchId, userId, sessionId) => {
      if (!socket) return;
      console.log(
        '[useMatchFlow] => initVerification => requesting aggregator or codeHolder'
      );
      socket.emit('initVerification', { matchId, userId, sessionId });
    },
    [socket]
  );

  /**
   * verifyCode: Sends the entered code to the server for verification.
   */
  const verifyCode = useCallback(
    (matchId, userId, codeAttempt, sessionId) => {
      if (!socket) return;
      console.log('[useMatchFlow] => verifyCode => codeAttempt=', codeAttempt);
      socket.emit('verifyCode', { matchId, userId, codeAttempt, sessionId });
    },
    [socket]
  );

  /**
   * verifyCodeStep: REST call for step-by-step verification.
   */
  const verifyCodeStep = useCallback(async (matchId, codeAttempt, partnerIndex) => {
    try {
      const { data } = await axios.post(
        `/matches/${matchId}/verify-code-step`,
        {
          codeAttempt,
          partnerIndex, // 0 or 1 for trio (ignored for 2p)
        }
      );
      return { ok: !!data?.ok };
    } catch (err) {
      const msg = err?.response?.data?.message || 'Unable to verify code';
      return { ok: false, message: msg };
    }
  }, []);

  /**
   * foundPartner: Notifies the server that this user found their partner.
   */
  const foundPartner = useCallback(
    (matchId, userId) => {
      if (!socket) return;
      console.log('[useMatchFlow] => foundPartner => Emitting to server');
      socket.emit('foundPartner', { matchId, userId });
    },
    [socket]
  );

  /**
   * endSession: Ends the current session/match.
   */
  const endSession = useCallback(
    (matchId, userId, sessionId) => {
      if (!socket) return;
      console.log('[useMatchFlow] => endSession => Emitting to server');
      socket.emit('endSession', { matchId, userId, sessionId });
    },
    [socket]
  );

  /**
   * matchUpdated => keep our local match in sync + notify enforcer
   */
  useEffect(() => {
    if (!socket) return;

    function handleMatchUpdated(updatedMatch) {
      if (match?._id && updatedMatch._id === match._id) {
        setMatch(updatedMatch);
      }
      try {
        window.dispatchEvent(new CustomEvent('sm:match-updated'));
      } catch {}
    }

    socket.on('matchUpdated', handleMatchUpdated);
    return () => socket.off('matchUpdated', handleMatchUpdated);
  }, [socket, match?._id, setMatch]);

  return {
    socket,
    match,
    setMatch,
    isFinding,
    setIsFinding,
    occupancy,
    setOccupancy,
    errorMsg,
    setErrorMsg,
    findPartner,
    cancelFind,
    respondMatch,
    initVerification,
    verifyCode,
    verifyCodeStep,
    foundPartner,
    endSession,
  };
}
